/**
 * V66: 조직 근로 현황 — 직원별 월간 출퇴근 기록 모달
 *
 * 기능:
 *  - 조직 근로 현황 페이지의 직원 이름 클릭 시 표시
 *  - 좌우 버튼(< / >)으로 이전월/다음월 이동, 그때마다 BE 재조회
 *  - BE: getMyAttendances callable (V66 확장: 조직장 scope 분기 추가됨)
 *
 * 권한:
 *  - BE에서 본인/HR-Admin scope/조직장 scope 검증. 권한 없는 직원 조회 시 permission-denied
 *
 * 정책:
 *  - 모달 닫으면 employeeId/month 초기화 (호출 측 onClose에서 제어)
 *  - 한 번에 1개월 1직원 조회 (BE startDate~endDate 범위 1개월 = 31일 이내)
 *
 * V70.8: 조직근로현황 집계기간과 동일 기간 조회
 *  - getMyAttendances 에 { month, periodMode:'orgWorkStatus' } 전달 → **BE가 대상 직원의 companyCode 로 기간 결정**
 *    (KR = 해당월 1일~말일, CN = 전월 16일~해당월 15일). FE 는 기간을 재계산하지 않는다.
 *  - 응답 period 를 그대로 표시하며, 달력월과 다를 때만 "집계기간 …" 한 줄 추가 (KR 은 기존 UI 동일)
 *  - 표는 BE 가 반환한 날짜 행을 그대로 나열하므로 두 달에 걸친 기간도 모두 표시됨
 *
 * V70.8c: 월 이동 상한을 목록과 동일하게 적용
 *  - 상한(`maxMonth`)은 **목록 페이지가 getOrgWorkStatusMonthly 응답(maxSelectableMonths)에서 받은 값을 그대로 전달**한다.
 *    (CN 전용 화면 = CN 귀속월, KR 전용·KR+CN 혼합 = 현재 달력월)
 *  - 모달은 귀속월/집계기간을 **계산하지 않는다**. 전달받은 문자열 비교만 수행.
 *  - 목록에서 막힌 미래 월로 상세를 통해 우회 이동할 수 없다.
 *
 * V70-S-4: 승인 근무 4개 시간 + 상태 배지 + 엑셀 다운로드 (조직·전사 근로 현황 공용)
 *  - getMyAttendances 에 includeApprovedWork:true → 서버가 승인 근무 신청을 연장 근무 관리와 같은 엔진
 *    (V70-S-2 상호 배타 분류)으로 일별 계산. 권한은 기존 getMyAttendances 검증 그대로.
 *  - 행 = 출퇴근 기록 날짜 ∪ 승인 근무 날짜. 출퇴근 근무시간과 승인 근무 4개 시간은 별개 컬럼 (덮어쓰지 않음)
 *  - 상태 텍스트·판정은 기존 그대로, 승인 근무 유형은 짧은 배지로 추가
 *  - 화면 행과 엑셀 행은 employeeDailyRecords.ts 의 같은 함수에서 만든다
 *  - CN 직원은 4개 분류(KR 정책) 미적용 → '해당 없음'
 * V70-S-5: 4개 시간 숫자는 포함 기준(연장 ⊇ 야간, 휴일 ⊇ 휴일 야간)으로 표시. 배지는 원본 분류 기준
 */

import React, { useEffect, useMemo, useState } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2, AlertCircle, Download } from 'lucide-react';
import * as XLSX from 'xlsx';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../../firebase/utils';
import { useModalOverlayClose } from '../../../shared/hooks/useModalOverlayClose';
import {
  ApprovedWorkResponse,
  DAILY_COLUMNS,
  DailyAttendance,
  buildDailyRecordSheet,
  buildDailyRows,
  dailyExportFileName,
  formatApprovedCell,
} from './employeeDailyRecords';
import { INCLUSIVE_WORK_NOTE } from '../../../shared/utils/approvedWorkDisplay';

interface EmployeeMonthlyAttendanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId: string | null;
  employeeName: string | null;
  /** 모달 열 때 보여줄 초기 월 (YYYY-MM). 페이지의 현재 월. */
  initialMonth: string;
  /**
   * V70.8c: 선택 가능한 최대 월 (YYYY-MM). 목록 페이지의 상한과 동일한 값.
   *  - BE getOrgWorkStatusMonthly 의 maxSelectableMonths 에서 유래 (FE 재계산 없음)
   *  - 미전달(구 호출부) 시 상한 없음 = 기존 동작
   */
  maxMonth?: string | null;
}

interface GetMyAttendancesResponse {
  success: boolean;
  attendances: DailyAttendance[];
  totalCount: number;
  /** V70.8: BE가 실제 조회에 사용한 집계기간 (KR=1일~말일, CN=전월16~당월15) */
  period?: { startDate: string; endDate: string };
  /** V70-S-4: includeApprovedWork 요청 시 일별 승인 근무시간 (CN = NOT_APPLICABLE) */
  approvedWork?: ApprovedWorkResponse;
}

function getMonthRange(month: string): { startDate: string; endDate: string } {
  const [y, m] = month.split('-').map(Number);
  const startDate = `${y}-${String(m).padStart(2, '0')}-01`;
  // 다음달 1일 - 1일 → 해당 월 마지막일
  const last = new Date(Date.UTC(y, m, 0)); // m: 1-based, Date.UTC m: 0-based, 0번째 일자 = 전월 마지막
  const endDate = `${last.getUTCFullYear()}-${String(last.getUTCMonth() + 1).padStart(2, '0')}-${String(last.getUTCDate()).padStart(2, '0')}`;
  return { startDate, endDate };
}

function addMonths(monthStr: string, delta: number): string {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthDisplay(monthStr: string): string {
  const [y, m] = monthStr.split('-').map(Number);
  return `${y}년 ${m}월`;
}

export function EmployeeMonthlyAttendanceModal({
  isOpen,
  onClose,
  employeeId,
  employeeName,
  initialMonth,
  maxMonth,
}: EmployeeMonthlyAttendanceModalProps) {
  const overlayProps = useModalOverlayClose(onClose);
  const [month, setMonth] = useState<string>(initialMonth);
  const [attendances, setAttendances] = useState<DailyAttendance[]>([]);
  // V70-S-4: 일별 승인 근무시간 (응답 직원·월과 함께 보관 — period 와 같은 이유)
  const [approvedWork, setApprovedWork] = useState<{ employeeId: string; month: string; data: ApprovedWorkResponse } | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * V70.8: BE가 실제 조회한 집계기간 (FE 재계산 없음)
   * V70.8e: **어느 직원(employeeId)·어느 월(month)의 응답인지 함께 보관**한다.
   *   month/employeeId 는 즉시 바뀌지만 period 는 응답이 와야 갱신되므로, 둘을 따로 두면
   *   - 월 이동 로딩 구간에서 "이전 월의 period" 가 "새 month" 와 비교되어 잘못 표시되고
   *   - 다른 직원 상세를 열 때 초기화 effect 가 돌기 전 한 프레임 동안 "이전 직원의 period" 가 남는다.
   */
  const [period, setPeriod] = useState<{ employeeId: string; month: string; startDate: string; endDate: string } | null>(null);

  // 모달 열릴 때 초기 월 동기화 + 직원 변경 시 초기화
  useEffect(() => {
    if (isOpen && employeeId) {
      setMonth(initialMonth);
      setAttendances([]);
      setApprovedWork(null); // V70-S-4
      setError(null);
      setPeriod(null); // V70.8: 직원 변경 시 이전 직원의 기간이 남지 않도록 초기화
    }
  }, [isOpen, employeeId, initialMonth]);

  // 월/직원 변경 시 BE 조회
  useEffect(() => {
    if (!isOpen || !employeeId) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        // V70.8: 집계기간은 BE가 대상 직원의 companyCode 로 결정 (KR=1일~말일, CN=전월16~당월15).
        //   startDate/endDate 는 periodMode 를 모르는 구 버전 BE 호환용 fallback (구 동작 = 달력월).
        const { startDate, endDate } = getMonthRange(month);
        const fn = httpsCallable<
          { employeeId: string; startDate: string; endDate: string; month: string; periodMode: 'orgWorkStatus'; includeApprovedWork: boolean },
          GetMyAttendancesResponse
        >(getFunctions(), 'getMyAttendances');
        // V70-S-4: includeApprovedWork — 일별 승인 근무시간 함께 조회 (권한 검증은 동일)
        const res = await fn({ employeeId, startDate, endDate, month, periodMode: 'orgWorkStatus', includeApprovedWork: true });
        if (cancelled) return;
        const list = (res.data?.attendances || []).slice().sort((a, b) =>
          a.date < b.date ? -1 : a.date > b.date ? 1 : 0
        );
        setAttendances(list);
        setApprovedWork(res.data?.approvedWork ? { employeeId, month, data: res.data.approvedWork } : null);
        // V70.8e: 응답이 어느 직원·어느 월의 것인지 함께 기록 (요청 시점 값을 그대로 사용)
        setPeriod(res.data?.period ? { employeeId, month, ...res.data.period } : null);
      } catch (err: any) {
        if (cancelled) return;
        console.error('[V66] getMyAttendances 실패:', err);
        const msg: string = err?.message || '조회 중 오류가 발생했습니다.';
        // 권한 에러는 사용자 친화적으로
        if (typeof msg === 'string' && /permission-denied|권한/i.test(msg)) {
          setError('해당 직원의 출퇴근 기록을 조회할 권한이 없습니다.');
        } else {
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [isOpen, employeeId, month]);

  /**
   * V70.8: 집계기간 표시 문자열
   *  - BE 응답 period 를 그대로 사용 (FE 재계산 없음)
   *  - 달력월(1일~말일)과 같으면 표시하지 않음 → KR 화면 기존 UI 유지
   * V70.8e: **현재 표시 중인 직원·월의 응답일 때만** 표시한다.
   *  - 월 이동 직후(로딩 중)에는 period 가 아직 이전 월 것이므로 표시하지 않는다.
   *    (이전 월의 KR 달력월 기간이 새 월과 불일치해 "집계기간 …" 이 잠깐 보이던 현상 차단)
   *  - 다른 직원 상세를 열 때도 초기화 effect 전에 이전 직원(CN)의 기간이 렌더되지 않는다.
   *  - 늦게 도착한 이전 요청의 응답도 직원·월이 달라 표시 대상이 되지 않는다.
   */
  const periodText = useMemo(() => {
    if (!period || period.month !== month || period.employeeId !== employeeId) return null;
    const calendar = getMonthRange(month);
    if (period.startDate === calendar.startDate && period.endDate === calendar.endDate) return null;
    return `집계기간 ${period.startDate.replace(/-/g, '.')} ~ ${period.endDate.replace(/-/g, '.')}`;
  }, [period, month, employeeId]);

  /**
   * V70.8c: 다음 월 이동 가능 여부
   *  - 상한은 목록 페이지가 전달한 maxMonth (BE maxSelectableMonths 유래). 문자열 비교만 수행.
   *  - maxMonth 가 없으면(구 호출부) 기존처럼 제한 없음
   */
  const canGoNext = !maxMonth || month < maxMonth;

  /** V70-S-4: 현재 직원·월의 승인 근무 응답만 사용 (월 이동 로딩 중 이전 월 값 혼입 방지) */
  const currentApproved = approvedWork && approvedWork.employeeId === employeeId && approvedWork.month === month ? approvedWork.data : null;
  const rows = useMemo(() => buildDailyRows(attendances, currentApproved), [attendances, currentApproved]);
  const notApplicableReason = currentApproved?.policy === 'NOT_APPLICABLE' ? currentApproved.reason : null;

  /** V70-S-4: 선택 직원 1명 · 선택 월 일일 기록 엑셀 (화면 행과 같은 함수) */
  const handleExcelDownload = () => {
    if (isExporting || loading || error) return;
    setIsExporting(true);
    try {
      const ws = buildDailyRecordSheet(XLSX, rows, {
        employeeName,
        month,
        period: period && period.employeeId === employeeId && period.month === month ? { startDate: period.startDate, endDate: period.endDate } : null,
        notApplicableReason,
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '일일근무기록');
      XLSX.writeFile(wb, dailyExportFileName(employeeName, month));
    } catch (err: any) {
      console.error('[V70-S-4] 직원 근무 기록 엑셀 실패:', err);
      alert(err?.message || '엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  if (!isOpen || !employeeId) return null;

  return (
    <div
      {...overlayProps}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        // V66.2: 모달 상단 위치 고정. 월 이동 시 내용 높이가 변해도 모달이 상하로 점프하지 않음
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '8vh',
        zIndex: 1000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px 24px',
          // V70-S-4: 4개 승인 근무 컬럼 추가로 720 → 1040 (화면보다 커지지 않게 95vw 상한, 좁으면 본문 가로 스크롤)
          width: '1040px',
          maxWidth: '95vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          // V66.1: flex 자식이 maxHeight 를 초과하더라도 화면 밖으로 새지 않도록 hidden 처리.
          // 본문 div(flex:1, minHeight:0, overflowY:auto) 가 안에서 스크롤을 책임짐.
          overflow: 'hidden',
        }}
      >
        {/* 헤더 — flexShrink: 0 으로 줄지 않게 고정 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexShrink: 0 }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, margin: 0 }}>
            {employeeName ? `${employeeName} 월별 출퇴근 기록` : '월별 출퇴근 기록'}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* V70-S-4: 선택 직원·선택 월 일일 기록 엑셀 */}
          <button
            onClick={handleExcelDownload}
            disabled={isExporting || loading || !!error}
            title="선택한 직원의 이 달 일일 근무 기록 엑셀 다운로드"
            style={{
              padding: '6px 12px',
              background: isExporting || loading || error ? 'var(--color-gray-400)' : 'var(--color-green-600)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isExporting || loading || error ? 'not-allowed' : 'pointer',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Download size={14} />
            {isExporting ? '다운로드 중...' : '엑셀'}
          </button>
          <button
            onClick={onClose}
            aria-label="close"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-gray-500)',
              padding: 4,
            }}
          >
            <X size={20} />
          </button>
          </div>
        </div>

        {/* 월 네비게이션 — flexShrink: 0 으로 줄지 않게 고정 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px', flexShrink: 0 }}>
          <button
            onClick={() => setMonth((m) => addMonths(m, -1))}
            disabled={loading}
            style={{
              background: 'transparent',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              padding: '6px 10px',
              cursor: loading ? 'default' : 'pointer',
              opacity: loading ? 0.5 : 1,
            }}
            aria-label="previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <div style={{ fontSize: '15px', fontWeight: 600, minWidth: '110px', textAlign: 'center' }}>
            {formatMonthDisplay(month)}
          </div>
          <button
            onClick={() => setMonth((m) => addMonths(m, 1))}
            disabled={loading || !canGoNext}
            style={{
              background: 'transparent',
              border: '1px solid var(--color-gray-300)',
              borderRadius: '8px',
              padding: '6px 10px',
              cursor: loading || !canGoNext ? 'default' : 'pointer',
              opacity: loading || !canGoNext ? 0.5 : 1,
            }}
            aria-label="next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>

        {/* V70.8: 집계기간 표시 — BE가 실제 조회한 기간이 달력월과 다를 때만 (CN: 전월16~당월15).
                   KR 은 기간 == 달력월 이라 표시되지 않아 기존 UI 그대로 유지됨 */}
        {periodText && (
          <div style={{ textAlign: 'center', fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '-6px', marginBottom: '12px', flexShrink: 0 }}>
            {periodText}
          </div>
        )}

        {/* V70-S-4: 4개 시간 기준 안내 */}
        <div data-testid="daily-approved-note" style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginBottom: '8px', flexShrink: 0 }}>
          {notApplicableReason
            ? `연장·야간·휴일·휴일 야간: ${notApplicableReason}`
            : `연장·야간·휴일·휴일 야간 = 승인된 근무 신청 시간 (자정·월 경계는 날짜별로 나눔). ${INCLUSIVE_WORK_NOTE} 근무시간 = 출퇴근 기록 기준`}
        </div>

        {/* 본문 — V66.2: 내용 높이만큼 자연스럽게 표시.
            flex:1 + minHeight:0 으로 부모(maxHeight:90vh) 한도 내에서 자라고, 넘치면 자체 overflowY 로 스크롤.
            31행 강제 영역(minHeight:1056) 제거 — 2월/기록 적은 달의 공백 과다 해결. */}
        {/* V70-S-4: 좁은 화면에서는 본문 가로 스크롤 (표 최소 폭) */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-gray-500)' }}>
              <Loader2 size={20} className="animate-spin" /> <span style={{ marginLeft: 8 }}>조회 중...</span>
            </div>
          ) : error ? (
            <div style={{ padding: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'var(--color-red-600)' }}>
              <AlertCircle size={28} style={{ marginBottom: 8 }} />
              <div style={{ fontSize: '14px' }}>{error}</div>
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--color-gray-500)', fontSize: '14px' }}>
              {periodText ? '해당 기간 출퇴근 기록이 없습니다.' : '해당 월 출퇴근 기록이 없습니다.'}
            </div>
          ) : (
            <table data-testid="daily-record-table" style={{ width: '100%', minWidth: '920px', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
              <colgroup>
                {DAILY_COL_WIDTHS.map((w, i) => <col key={i} style={{ width: `${w}px` }} />)}
              </colgroup>
              <thead>
                <tr style={{ background: 'var(--color-gray-100)', borderBottom: '2px solid var(--color-gray-200)' }}>
                  {DAILY_COLUMNS.map((c) => <th key={c} style={th}>{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} data-testid="daily-record-row" style={{ borderBottom: '1px solid var(--color-gray-100)' }}>
                    <td style={tdNum}>{r.date}</td>
                    <td style={td}>{r.dayLabel}</td>
                    <td style={tdNum}>{r.checkIn}</td>
                    <td style={tdNum}>{r.checkOut}</td>
                    <td style={tdNum}>{r.workTime}</td>
                    <td style={{ ...td, color: r.late ? 'var(--color-red-600)' : 'var(--color-gray-400)' }}>{r.late ? '지각' : '-'}</td>
                    <td style={tdNum}>{formatApprovedCell(r.overtimeMinutes)}</td>
                    <td style={tdNum}>{formatApprovedCell(r.nightMinutes)}</td>
                    <td style={tdNum}>{formatApprovedCell(r.holidayMinutes)}</td>
                    <td style={tdNum}>{formatApprovedCell(r.holidayNightMinutes)}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>
                      <span style={{ color: r.status.color }}>{r.status.text}</span>
                      {r.badges.length > 0 && (
                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '3px', marginLeft: '4px', verticalAlign: 'middle' }}>
                          {r.badges.map((b) => (
                            <span key={b} data-testid="daily-work-badge" style={b === '예외' ? badgeWarn : badge}>{b}</span>
                          ))}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

const th: React.CSSProperties = {
  padding: '8px 6px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-gray-700)',
};

const td: React.CSSProperties = {
  padding: '8px 6px',
  textAlign: 'center',
  fontSize: '13px',
  color: 'var(--color-gray-700)',
};

/** V70-S-4: 날짜·요일·출근·퇴근·근무시간·지각·연장·야간·휴일·휴일야간·상태 (합 920 = 표 최소 폭) */
const DAILY_COL_WIDTHS = [92, 40, 56, 56, 84, 44, 84, 84, 84, 96, 200];

const tdNum: React.CSSProperties = {
  ...td,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const badge: React.CSSProperties = {
  display: 'inline-block',
  padding: '1px 6px',
  borderRadius: '999px',
  fontSize: '11px',
  lineHeight: '16px',
  whiteSpace: 'nowrap',
  background: 'var(--color-blue-50, #eff6ff)',
  color: 'var(--color-blue-700, #1d4ed8)',
  border: '1px solid var(--color-blue-200, #bfdbfe)',
};

const badgeWarn: React.CSSProperties = {
  ...badge,
  background: 'var(--color-orange-50, #fff7ed)',
  color: 'var(--color-orange-700, #c2410c)',
  border: '1px solid var(--color-orange-200, #fed7aa)',
};
